import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Banner } from '@shared/entities/banner';
import { Paginate } from '@shared/entities/paginate';
import { ApiService, Pagination, PER_PAGE } from '@shared/services';
import { BehaviorSubject, Observable, Subject, catchError, map, of, shareReplay, switchMap, take, tap } from 'rxjs';

const LOG_PREFIX = '[Api][Banner] ';

export interface BannerFilterURL {
  keyword?: string;
  geoLocation?: string;
  operatingSector?: string;
  date?: string;
  direction?: 'ASC' | 'DESC';
  id?: string;
  orderBy?: string;
  role?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BannerService extends ApiService<Banner> {
  private _refresh$: Subject<Pagination>;
  allBanners$: Observable<Paginate<Banner>>;
  private _currentPaginator: Pagination;
  private _currentFilters: BannerFilterURL = {};

  set filters(value: BannerFilterURL) {
    this._currentFilters = value;
    this.refresh(1, PER_PAGE);
  }

  constructor(private _activatedRoute: ActivatedRoute) {
    const masterUrl = '/banners';
    const detailUrl = '/banners';

    super(Banner, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this.allBanners$ = this._refresh$.pipe(
      switchMap((pagination) => {
        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }
        if (this._currentFilters.keyword !== undefined) {
          this.setUrlParameter('keyword', this._currentFilters.keyword);
        }
        if (this._currentFilters.geoLocation !== undefined) {
          this.setUrlParameter('geoLocation', this._currentFilters.geoLocation);
        }
        if (this._currentFilters.operatingSector !== undefined) {
          this.setUrlParameter('operatingSector', this._currentFilters.operatingSector);
        }
        if (this._currentFilters.date !== undefined && this._currentFilters.date !== '') {
          this.setUrlParameter('date', this._currentFilters.date);
        }
        if (this._currentFilters.id !== undefined) {
          this.setUrlParameter('id', this._currentFilters.id);
        }
        if (this._currentFilters.role !== undefined) {
          this.setUrlParameter('role', this._currentFilters.role);
        }
        this.setUrlParameter('order_by', this._currentFilters.orderBy || 'createdDate');
        if (this._currentFilters.orderBy) {
          if (this._currentFilters.orderBy === 'createdDate') {
            this.setUrlParameter('direction', 'ASC');
          } else if (this._currentFilters.orderBy === 'modifiedDate') {
            this.setUrlParameter('direction', 'DESC');
          } else if (this._currentFilters.orderBy === 'startDate') {
            this.setUrlParameter('direction', 'ASC');
          } else if (this._currentFilters.orderBy === 'endDate') {
            this.setUrlParameter('direction', 'ASC');
          } else {
            this.setUrlParameter('direction', 'DESC');
          }
        }
        return this.paginate(Banner, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ banners', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ banners error', err)
      }),
      shareReplay(1)
    );
  }

  refresh(page: number, perPage: number = PER_PAGE) {
    this.logger.debug(LOG_PREFIX + 'refresh banners');
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this.allBanners$.pipe(take(1));
  }

  refreshFilters() {
    this.logger.debug(LOG_PREFIX + 'refresh filters');
    this._currentFilters = {};
    this.refresh(1, PER_PAGE);
  }

  getHomepageBanner(filters?: { role?: string; date?: string }): Observable<Banner[]> {
    this.logger.debug(LOG_PREFIX + 'getAllBanners with filters', filters);

    this.getUrlParameters().forEach((_, key) => this.removeUrlParameter(key));

    if (filters) {
      if (filters.role) {
        this.setUrlParameter('role', filters.role);
      }

      if (filters.date) {
        this.setUrlParameter('date', filters.date);
      }
    }

    return this.paginate(Banner, 1, 200).pipe(
      map((paginate) => paginate.data || []),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'getAllBanners error', error);
        return of([]);
      })
    );
  }
}
